import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignShareCollaborator,
  CollaboratorAccess,
  fetchPartnershipCollaborators,
  fetchReceivedShares,
  CollaboratorRight,
  ReceivedShare,
  setCollaboratorRight,
  unassignShareCollaborator,
} from "../../../api/partnership";
import { Avatar } from "../../common/Avatar";
import { Button } from "../../common/Button";
import { Modal, ModalActions } from "../../common/Modal";

type AssignCollaboratorsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  partnershipId: number | null;
  /** Nom de l'entreprise cliente, pour situer les ressources concernées. */
  companyName: string;
};

/**
 * Gestion des accès de l'ÉQUIPE sur un partenariat.
 *
 * L'écran part du COLLABORATEUR, pas de la ressource : la question que se pose un
 * installateur est « qui, chez moi, s'occupe de ce client ? », pas l'inverse. Une ligne
 * par personne, dépliable, avec son niveau d'accès résumé — la version précédente
 * imposait de choisir une ressource, revenir, en choisir une autre.
 *
 * Deux niveaux d'accès :
 *  - **complet** : tout ce que ce client partage, y compris ce qu'il partagera plus tard ;
 *  - **ciblé** : uniquement les ressources cochées.
 *
 * L'installateur lui-même n'apparaît pas : il a déjà accès à tout, par définition.
 */

/** Libellé du niveau, affiché en pastille à droite de chaque ligne. */
const RESOURCE_LABEL: Record<string, string> = {
  breeding: "Élevage",
  building: "Bâtiment",
  device: "Produit",
};

const RESOURCE_ICON: Record<string, "home" | "building" | "cogs"> = {
  breeding: "home",
  building: "building",
  device: "cogs",
};

/**
 * Une ressource partagée, replacée dans sa hiérarchie.
 *
 * L'arborescence est reconstruite depuis `id_breeding` / `id_building` : un élevage et un
 * produit qu'il contient s'afficheraient sinon côte à côte, et l'installateur ne verrait
 * pas que cocher l'élevage couvre déjà le produit.
 */
type ShareNode = {
  share: ReceivedShare;
  children: ShareNode[];
};

/**
 * Construit la hiérarchie des ressources reçues.
 *
 * Une ressource dont le parent n'est PAS lui-même partagé remonte à la racine : elle doit
 * rester cochable, même si son élevage n'a pas été partagé.
 */
const buildShareTree = (shares: ReceivedShare[]): ShareNode[] => {
  const byBreeding = new Map<number, ReceivedShare>();
  const byBuilding = new Map<number, ReceivedShare>();
  shares.forEach((s) => {
    if (s.resource_type === "breeding") byBreeding.set(s.id_resource, s);
    if (s.resource_type === "building") byBuilding.set(s.id_resource, s);
  });

  const nodes = new Map<number, ShareNode>();
  shares.forEach((s) => nodes.set(s.id_share, { share: s, children: [] }));

  const roots: ShareNode[] = [];
  shares.forEach((s) => {
    const node = nodes.get(s.id_share)!;
    // Le parent le PLUS PROCHE qui soit lui-même partagé : bâtiment d'abord, puis élevage.
    const parent =
      (s.id_building && byBuilding.get(s.id_building)) ||
      (s.id_breeding && byBreeding.get(s.id_breeding)) ||
      undefined;

    if (parent && parent.id_share !== s.id_share) {
      nodes.get(parent.id_share)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list: ShareNode[]): ShareNode[] =>
    list
      .sort((a, b) => a.share.label.localeCompare(b.share.label))
      .map((n) => ({ ...n, children: sortNodes(n.children) }));

  return sortNodes(roots);
};

/**
 * Une ligne de l'arborescence, et ses enfants.
 *
 * `inheritedFrom` porte le nom du parent coché : la ressource est alors déjà couverte,
 * sa case est cochée et désactivée (la décocher n'aurait aucun effet, l'accès viendrait
 * toujours du parent) et on affiche d'où vient l'accès — sans quoi l'installateur croit
 * à une incohérence.
 */
const ShareTreeRow = ({
  node,
  depth,
  collaborator,
  busyKey,
  onToggle,
  inheritedFrom,
}: {
  node: ShareNode;
  depth: number;
  collaborator: CollaboratorAccess;
  busyKey: string | null;
  onToggle: (c: CollaboratorAccess, s: ReceivedShare) => void;
  inheritedFrom: string | null;
}) => {
  const { share, children } = node;
  const assignedDirectly = collaborator.assigned_share_ids.includes(
    share.id_share
  );
  const covered = !!inheritedFrom;
  const key = `share-${collaborator.id}-${share.id_share}`;

  return (
    <>
      <label
        style={{ marginLeft: `${depth * 1}rem` }}
        className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
          assignedDirectly || covered
            ? "border-secondary bg-secondary/10"
            : "border-grey-light hover:bg-grey-light"
        } ${busyKey === key ? "opacity-50" : ""} ${
          covered ? "opacity-70 cursor-default" : "cursor-pointer"
        }`}
      >
        <input
          type="checkbox"
          checked={assignedDirectly || covered}
          disabled={busyKey === key || covered}
          onChange={() => onToggle(collaborator, share)}
          className="w-4 h-4 accent-secondary cursor-pointer shrink-0"
        />
        <FontAwesomeIcon
          icon={RESOURCE_ICON[share.resource_type] ?? "cogs"}
          className="text-grey-readable shrink-0"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-default-text truncate">{share.label}</span>
          <span className="block text-xs text-grey-readable truncate">
            {covered ? `Inclus dans ${inheritedFrom}` : share.sublabel}
          </span>
        </span>
        <span className="shrink-0 text-xs text-grey-readable hidden md:inline">
          {RESOURCE_LABEL[share.resource_type] ?? share.resource_type}
        </span>
      </label>

      {children.map((child) => (
        <ShareTreeRow
          key={child.share.id_share}
          node={child}
          depth={depth + 1}
          collaborator={collaborator}
          busyKey={busyKey}
          onToggle={onToggle}
          // L'héritage se propage : si un parent est coché, tous ses descendants le sont.
          inheritedFrom={
            inheritedFrom ?? (assignedDirectly ? share.label : null)
          }
        />
      ))}
    </>
  );
};

export const AssignCollaboratorsModal = ({
  isOpen,
  onClose,
  partnershipId,
  companyName,
}: AssignCollaboratorsModalProps) => {
  const [collaborators, setCollaborators] = useState<CollaboratorAccess[]>([]);
  const [shares, setShares] = useState<ReceivedShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!partnershipId) return;
    setLoading(true);
    // Deux appels en parallèle : ni l'un ni l'autre ne dépend du résultat de son voisin.
    const [team, received] = await Promise.all([
      fetchPartnershipCollaborators(partnershipId),
      fetchReceivedShares(partnershipId),
    ]);
    setCollaborators(team.collaborators);
    setShares(received.shares);
    setError(team.error || received.error);
    setLoading(false);
  }, [partnershipId]);

  useEffect(() => {
    if (isOpen) {
      setExpanded(null);
      setSearch("");
      load();
    }
  }, [isOpen, load]);

  const shareTree = useMemo(() => buildShareTree(shares), [shares]);

  const visibleCollaborators = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return collaborators;
    return collaborators.filter((c) =>
      `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(term)
    );
  }, [collaborators, search]);

  const handleRight = async (
    collaborator: CollaboratorAccess,
    right: CollaboratorRight
  ) => {
    if (!partnershipId) return;
    const current = collaborator[right];
    const key = `${right}-${collaborator.id}`;
    setBusyKey(key);
    setError(null);
    const result = await setCollaboratorRight(
      partnershipId,
      collaborator.id,
      right,
      !current
    );
    setBusyKey(null);

    if (!result.success) {
      setError(result.error);
      return;
    }
    setCollaborators((prev) =>
      prev.map((c) => (c.id === collaborator.id ? { ...c, [right]: !current } : c))
    );
  };

  const handleShare = async (
    collaborator: CollaboratorAccess,
    share: ReceivedShare
  ) => {
    if (!partnershipId) return;
    const assigned = collaborator.assigned_share_ids.includes(share.id_share);
    const key = `share-${collaborator.id}-${share.id_share}`;
    setBusyKey(key);
    setError(null);
    const result = assigned
      ? await unassignShareCollaborator(
          partnershipId,
          share.id_share,
          collaborator.id
        )
      : await assignShareCollaborator(
          partnershipId,
          share.id_share,
          collaborator.id
        );
    setBusyKey(null);

    if (!result.success) {
      setError(result.error);
      return;
    }
    setCollaborators((prev) =>
      prev.map((c) =>
        c.id === collaborator.id
          ? {
              ...c,
              assigned_share_ids: assigned
                ? c.assigned_share_ids.filter((id) => id !== share.id_share)
                : [...c.assigned_share_ids, share.id_share],
            }
          : c
      )
    );
  };

  /** Résumé affiché sur la ligne repliée — l'information la plus utile en un coup d'œil. */
  const accessSummary = (c: CollaboratorAccess) => {
    if (c.full_access) {
      return { label: "Accès complet", tone: "bg-secondary text-tertiary" };
    }
    // On compte les ressources cochées EXPLICITEMENT : cocher un élevage couvre ses
    // bâtiments et produits, mais annoncer « 7 ressources » pour un seul clic serait
    // trompeur — l'arborescence montre déjà l'étendue réelle.
    const count = c.assigned_share_ids.length;
    if (count === 0) {
      return { label: "Aucun accès", tone: "bg-grey-light text-grey-readable" };
    }
    return {
      label: `${count} ressource${count > 1 ? "s" : ""}`,
      tone: "bg-grey-light text-tertiary",
    };
  };

  /** Pastille secondaire : le droit d'associer se lit indépendamment du niveau d'accès. */
  const canAssociateBadge = (c: CollaboratorAccess) =>
    c.can_associate ? "Peut associer" : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Accès de mon équipe"
      icon="users"
      maxWidth="lg"
    >
      <div className="py-1">
        <p className="text-grey-readable mb-4 text-sm md:text-base">
          Définissez qui, dans votre équipe, accède aux ressources de{" "}
          <strong className="text-default-text">{companyName}</strong>. Vous y avez
          accès sans figurer dans cette liste.
        </p>

        {error && (
          <div
            role="alert"
            className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm"
          >
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-grey-readable py-6 text-center">Chargement…</p>
        ) : collaborators.length === 0 ? (
          <div className="py-8 text-center text-grey-readable">
            <FontAwesomeIcon icon="users" size="2x" className="mb-3" />
            <p className="text-default-text font-oxaniumMedium">
              Aucun collaborateur
            </p>
            <p className="text-sm mt-1">
              Ajoutez des collaborateurs à votre entreprise pour leur déléguer
              l'accès à ce client.
            </p>
          </div>
        ) : (
          <>
            {/* La recherche n'apparaît qu'au-delà de 5 personnes : en dessous, elle
                ajoute du bruit sans rien résoudre. */}
            {collaborators.length > 5 && (
              <div className="relative mb-3">
                <FontAwesomeIcon
                  icon="magnifying-glass"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-readable"
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un collaborateur…"
                  aria-label="Rechercher un collaborateur"
                  className="w-full rounded-lg border border-grey-medium pl-10 pr-4 py-2 focus:outline-none focus:border-tertiary"
                />
              </div>
            )}

            <ul className="flex flex-col gap-2 max-h-[26rem] overflow-y-auto pr-1">
              {visibleCollaborators.map((collaborator) => {
                const isOpen = expanded === collaborator.id;
                const summary = accessSummary(collaborator);
                const fullName =
                  `${collaborator.first_name} ${collaborator.last_name}`.trim() ||
                  collaborator.email;

                return (
                  <li
                    key={collaborator.id}
                    className={`rounded-lg border transition-colors ${
                      collaborator.full_access
                        ? "border-secondary"
                        : "border-grey-medium"
                    }`}
                  >
                    {/* En-tête cliquable : toute la ligne déplie, pas seulement le chevron */}
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded(isOpen ? null : collaborator.id)
                      }
                      aria-expanded={isOpen}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-grey-light transition-colors rounded-lg"
                    >
                      <div className="shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-grey-light">
                        <Avatar
                          value={collaborator.avatar}
                          firstName={collaborator.first_name}
                          lastName={collaborator.last_name}
                          emojiClassName="text-xl"
                          initialsClassName="text-sm"
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="font-oxaniumMedium text-default-text truncate">
                          {fullName}
                        </p>
                        <p className="text-sm text-grey-readable truncate">
                          {collaborator.email}
                        </p>
                      </div>

                      {/* Le résumé passe sous le nom en mobile : sur un écran étroit,
                          nom + email + pastille + chevron ne tiennent pas sur une ligne. */}
                      <span className="hidden md:flex shrink-0 items-center gap-1">
                        {canAssociateBadge(collaborator) && (
                          <span className="px-2 py-1 rounded-full text-xs font-oxaniumMedium bg-grey-light text-grey-readable">
                            <FontAwesomeIcon icon="plus" className="mr-1" />
                            {canAssociateBadge(collaborator)}
                          </span>
                        )}
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-oxaniumMedium ${summary.tone}`}
                        >
                          {summary.label}
                        </span>
                      </span>

                      <FontAwesomeIcon
                        icon="chevron-down"
                        className={`shrink-0 text-grey-readable transition-transform ${
                          isOpen ? "" : "-rotate-90"
                        }`}
                      />
                    </button>

                    <div className="md:hidden px-3 pb-2 -mt-1">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-oxaniumMedium ${summary.tone}`}
                      >
                        {summary.label}
                      </span>
                    </div>

                    {isOpen && (
                      <div className="px-3 pb-3 border-t border-grey-light pt-3">
                        {/* Accès complet : mis en avant, c'est le choix le plus courant
                            pour un collaborateur qui suit un client de bout en bout. */}
                        <div className="flex flex-col gap-2">
                          <label
                            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              collaborator.full_access
                                ? "bg-secondary/15"
                                : "bg-grey-light"
                            } ${
                              busyKey === `full_access-${collaborator.id}`
                                ? "opacity-50"
                                : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={collaborator.full_access}
                              disabled={
                                busyKey === `full_access-${collaborator.id}`
                              }
                              onChange={() =>
                                handleRight(collaborator, "full_access")
                              }
                              className="mt-0.5 w-5 h-5 accent-secondary cursor-pointer shrink-0"
                            />
                            <span className="min-w-0">
                              <span className="block font-oxaniumMedium text-default-text">
                                Accès complet à ce client
                              </span>
                              <span className="block text-sm text-grey-readable">
                                Tout ce que {companyName} partage, y compris les
                                ressources ajoutées plus tard.
                              </span>
                            </span>
                          </label>

                          {/* Droit indépendant de l'accès complet : on peut confier la
                              déclaration de matériel sans donner la vue sur tout. */}
                          <label
                            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              collaborator.can_associate
                                ? "bg-secondary/15"
                                : "bg-grey-light"
                            } ${
                              busyKey === `can_associate-${collaborator.id}`
                                ? "opacity-50"
                                : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={collaborator.can_associate}
                              disabled={
                                busyKey === `can_associate-${collaborator.id}`
                              }
                              onChange={() =>
                                handleRight(collaborator, "can_associate")
                              }
                              className="mt-0.5 w-5 h-5 accent-secondary cursor-pointer shrink-0"
                            />
                            <span className="min-w-0">
                              <span className="block font-oxaniumMedium text-default-text">
                                Peut associer des produits
                              </span>
                              <span className="block text-sm text-grey-readable">
                                Autorise à déclarer un nouveau produit chez{" "}
                                {companyName}.
                              </span>
                            </span>
                          </label>
                        </div>

                        {shares.length === 0 ? (
                          <p className="mt-3 text-sm text-grey-readable">
                            Ce client ne vous a encore partagé aucune ressource.
                          </p>
                        ) : (
                          <div
                            className={`mt-3 ${
                              collaborator.full_access
                                ? "opacity-50 pointer-events-none"
                                : ""
                            }`}
                            // Accès complet actif : les cases ciblées n'ont plus d'effet.
                            // On les neutralise sans les vider — désactiver l'accès
                            // complet doit rétablir exactement l'état précédent.
                            aria-hidden={collaborator.full_access}
                          >
                            <p className="text-sm text-grey-readable mb-2">
                              {collaborator.full_access
                                ? "Accès détaillé (inactif tant que l'accès complet est donné)"
                                : "Ou choisissez des ressources précises :"}
                            </p>

                            {shareTree.map((node) => (
                              <ShareTreeRow
                                key={node.share.id_share}
                                node={node}
                                depth={0}
                                collaborator={collaborator}
                                busyKey={busyKey}
                                onToggle={handleShare}
                                inheritedFrom={null}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {visibleCollaborators.length === 0 && (
              <p className="py-6 text-center text-grey-readable">
                Aucun collaborateur ne correspond à « {search} ».
              </p>
            )}
          </>
        )}

        <ModalActions>
          <Button onClick={onClose}>Fermer</Button>
        </ModalActions>
      </div>
    </Modal>
  );
};
